import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Xác nhận',
  cancelText = 'Hủy',
  isDestructive = false,
  isLoading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isLoading ? onClose : undefined}
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-sm overflow-hidden rounded-md bg-white p-6 shadow-sm"
          >
            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <div className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
                isDestructive ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
              }`}>
                <i className={`${
                  isDestructive ? 'ri-error-warning-line' : 'ri-question-line'
                } text-2xl`} />
              </div>

              <h3 className="mb-2 sm:text-sm text-xs text-slate-900">{title}</h3>
              <div className="mb-6 sm:text-sm text-xs text-slate-500">
                {message}
              </div>

              <div className="flex w-full gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="h-10 flex-1 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 transition-all hover:bg-slate-50 active:scale-95 disabled:opacity-50"
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className={`h-10 flex-1 rounded-md sm:text-sm text-xs text-white transition-all active:scale-95 disabled:opacity-50 ${
                    isDestructive 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {isLoading ? (
                    <i className="ri-loader-4-line animate-spin text-lg" />
                  ) : (
                    confirmText
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

